import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import {
  Button,
  Sheet,
  SheetBody,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@nexus/ui";
import { vendorDetailsQueryOptions, useResignConnection } from "@/lib/queries";
import { TermsSignaturePanel } from "./TermsSignaturePanel";
import { isValidSignerName } from "../lib/signer-name";

interface ReSignSheetProps {
  connectionId: string;
  vendorId: string;
  tenantId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function ReSignSheet({
  connectionId,
  vendorId,
  tenantId,
  open,
  onClose,
  onSuccess,
}: ReSignSheetProps) {
  const { t } = useTranslation();
  const { data: vendor } = useQuery({
    ...vendorDetailsQueryOptions(vendorId),
    enabled: open && !!vendorId,
  });
  const resignMutation = useResignConnection();

  const [termsScrolled, setTermsScrolled] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [signerName, setSignerName] = useState("");

  const hasTerms = !!vendor?.termsMarkdown?.trim();

  useEffect(() => {
    if (!open) {
      setTermsScrolled(false);
      setTermsAccepted(false);
      setSignerName("");
      resignMutation.reset();
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const effectiveScrolled = !hasTerms || termsScrolled;
  const canSubmit =
    effectiveScrolled && termsAccepted && isValidSignerName(signerName);

  async function handleSubmit() {
    if (!canSubmit) return;
    await resignMutation.mutateAsync({
      connectionId,
      payload: { tenantId, signerName: signerName.trim() },
    });
    onSuccess();
    onClose();
  }

  return (
    <Sheet open={open} onOpenChange={(o) => !o && !resignMutation.isPending && onClose()}>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>
            {vendor
              ? t("marketplace.resign.title", { vendorName: vendor.name })
              : t("marketplace.resign.titleLoading")}
          </SheetTitle>
        </SheetHeader>

        <SheetBody className="flex flex-col gap-5">
          <p className="text-sm text-muted-foreground">{t("marketplace.resign.description")}</p>

          {vendor && (
            <TermsSignaturePanel
              idPrefix="resign"
              vendorName={vendor.name}
              termsMarkdown={vendor.termsMarkdown}
              termsVersion={vendor.termsVersion}
              signerName={signerName}
              termsScrolled={termsScrolled}
              termsAccepted={termsAccepted}
              onScrolled={() => setTermsScrolled(true)}
              onAcceptedChange={setTermsAccepted}
              onSignerNameChange={setSignerName}
            />
          )}
        </SheetBody>

        <SheetFooter className="justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={resignMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
          <Button
            type="button"
            disabled={!canSubmit || resignMutation.isPending}
            onClick={() => void handleSubmit()}
          >
            {resignMutation.isPending
              ? t("marketplace.resign.submitting")
              : t("marketplace.resign.submit")}
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
